import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { first } from 'rxjs/operators';
import { CandidateListService, CoreService, AuthenticationService } from 'src/app/shared';

@Component({
  selector: 'app-candidate-biometrics-inline',
  templateUrl: './candidate-biometrics-inline.component.html',
  styleUrls: ['./candidate-biometrics-inline.component.scss']
})
export class CandidateBiometricsInlineComponent implements OnChanges {
  @Input() candidateId: string;
  @Input() batchId: string;

  metrics = [
    { key: 'photo', label: 'Photo' },
    { key: 'left_finger', label: 'Left Finger' },
    { key: 'right_finger', label: 'Right Finger' },
    { key: 'left_IRIS', label: 'Left IRIS' },
    { key: 'right_IRIS', label: 'Right IRIS' },
  ];

  data: any[] = [];
  loading = false;
  error = '';
  showExamDay = false;
  loadingExamDay = false;
  examDayImages: { key: string; label: string; imageSrc: string }[] = [];

  constructor(
    private candidateService: CandidateListService,
    private coreService: CoreService,
    private auth: AuthenticationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.candidateId || changes.batchId) && this.candidateId && this.batchId) {
      this.loadAll();
    }
  }

  loadAll(): void {
    this.loading = true;
    this.error = '';
    this.data = [];

    const tasks = this.metrics.map(m => this.loadMetric(m));
    Promise.all(tasks)
      .then(results => {
        this.data = results;
        this.loading = false;
      })
      .catch(() => {
        this.error = 'Unable to load biometrics.';
        this.loading = false;
      });
  }

  private async loadMetric(metric: { key: string; label: string }) {
    return this.candidateService.getVerificationMetricData(this.candidateId, metric.key, this.batchId)
      .pipe(first())
      .toPromise()
      .then((res: any) => {
        const metricData = res.metric_data || '';
        return {
          key: metric.key,
          label: metric.label,
          isRejected: metric.key === 'rejected',
          created: res.created,
          matchResult: res.match_result,
          matchScore: res.match_score,
          raw: metricData,
          imageSrc: metric.key === 'rejected' ? '' : this.normalizeBase64(metricData)
        };
      })
      .catch(() => {
        return { key: metric.key, label: metric.label, isRejected: false, raw: '', imageSrc: '', created: null, matchResult: null, matchScore: null };
      });
  }

  private normalizeBase64(base64Data: string): string {
    if (!base64Data) {
      return '';
    }
    if (base64Data.startsWith('data:')) {
      return base64Data;
    }
    return `data:image/png;base64,${base64Data}`;
  }

  toggleExamDay(): void {
    if (this.showExamDay) {
      this.showExamDay = false;
      return;
    }
    this.loadExamDay();
  }

  private loadExamDay(): void {
    this.loadingExamDay = true;
    const mapDoc: Record<string, string> = {
      photo: 'exam_photo',
      left_finger: 'exam_left_thumb',
      right_finger: 'exam_right_thumb',
      left_IRIS: 'exam_left_iris',
      right_IRIS: 'exam_right_iris'
    };
    this.examDayImages = this.metrics.map(m => ({
      key: m.key,
      label: m.label,
      imageSrc: this.getDocUrl(this.candidateId, mapDoc[m.key])
    }));
    this.loadingExamDay = false;
    this.showExamDay = true;
  }

  private getDocUrl(id: string, docType: string): string {
    if (!id || !docType) {
      return '';
    }
    let url = this.coreService.getRootPath() + `/documents/${id}/${docType}`;
    url += `?appname=${this.coreService.getAppName()}`;
    url += `&usertype=${this.coreService.getUserType()}`;
    url += `&token=${this.auth.getAuthToken()}`;
    return url;
  }
}
